import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExpenseTags from './ExpenseTags'

const defaultExpense = {
	expenseName: '',
	supplierName: '',
	invoiceNumber: '',
	remarks: '',
	paymentMethod: 'cash',
	status: 'draft',
	expenseDate: moment().format('YYYY-MM-DD'),
	expenseSubtotal: '',
	expensePercentTax: '5',
	expenseTaxValue: '',
	expenseTotal: '',
	forVat: false,
	forCorporateTax: false,
	tags: [],
}

export default function ExpenseForm({ initialData, onSubmit, onCancel, submitting = false, mode = 'add' }) {
	const { t } = useTranslation()
	const [form, setForm] = useState(defaultExpense)
	const [selectedTags, setSelectedTags] = useState([])
	const isEditMode = mode === 'edit'

	const labels = {
		expenseName: t('expenses.form.fields.expenseName'),
		supplier: t('expenses.form.fields.supplier'),
		invoiceNumber: t('expenses.form.fields.invoiceNumber'),
		paymentMethod: t('expenses.form.fields.paymentMethod'),
		status: t('expenses.form.fields.status'),
		expenseTags: t('expenses.form.fields.expenseTags'),
		expenseDate: t('expenses.form.fields.expenseDate'),
		subtotal: t('expenses.form.fields.subtotal'),
		taxPercent: t('expenses.form.fields.taxPercent'),
		taxValue: t('expenses.form.fields.taxValue'),
		total: t('expenses.form.fields.total'),
		remarks: t('expenses.form.fields.remarks'),
		forVat: t('expenses.form.fields.forVat'),
		forCorporateTax: t('expenses.form.fields.forCorporateTax'),
	}

	const paymentOptions = [
		{ value: 'card', label: t('expenses.form.paymentOptions.card') },
		{ value: 'cash', label: t('expenses.form.paymentOptions.cash') },
		{ value: 'transfer', label: t('expenses.form.paymentOptions.transfer') },
		{ value: 'cheque', label: t('expenses.form.paymentOptions.cheque') },
		{ value: 'other', label: t('expenses.form.paymentOptions.other') },
	]

	const statusOptions = [
		{ value: 'paid', label: t('expenses.form.statusOptions.paid') },
		{ value: 'due', label: t('expenses.form.statusOptions.due') },
		{ value: 'draft', label: t('expenses.form.statusOptions.draft') },
	]

	console.log(`TCL -> ExpenseForm.jsx:44 -> ExpenseForm -> form:`, form)

	useEffect(() => {
		const normalized = {
			...defaultExpense,
			...(initialData || {}),
		}
		normalized.expenseDate = moment(normalized.expenseDate).isValid() ? moment(normalized.expenseDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
		setForm(normalized)
		setSelectedTags(normalized?.tags.map(tag => ({ value: tag, label: tag })) || [])
	}, [initialData])

	// recompute tax value/total when subtotal or percent change
	useEffect(() => {
		const subtotal = Number(form.expenseSubtotal)
		const percent = Number(form.expensePercentTax)
		if (!Number.isFinite(subtotal) || !Number.isFinite(percent)) return
		const taxVal = +(subtotal * (percent / 100)).toFixed(2)
		const totalVal = +(subtotal + taxVal).toFixed(2)
		if (taxVal === form.expenseTaxValue && totalVal === form.expenseTotal) return
		setForm(prev => ({ ...prev, expenseTaxValue: taxVal, expenseTotal: totalVal }))
	}, [form.expenseSubtotal, form.expensePercentTax, form.expenseTaxValue, form.expenseTotal])

	const handleChange = (field, value) => {
		setForm(prev => ({ ...prev, [field]: value }))
	}

	const handleCheckbox = (field, checked) => {
		setForm(prev => ({ ...prev, [field]: checked }))
	}

	const handleTagsChange = value => {
		setSelectedTags(value || []);
		const tags = value ? value.map(option => option.value) : [];
		setForm(prev => ({ ...prev, tags }));
	}

	const handleSubmit = e => {
		e.preventDefault()
		onSubmit(form)
	}

	const submitLabel = isEditMode ? t('expenses.edit.submit') : t('expenses.add.submit')
	const submittingLabel = isEditMode ? t('expenses.edit.submitting') : t('expenses.add.submitting')
	const cancelLabel = isEditMode ? t('expenses.edit.cancel') : t('expenses.add.cancel')
	const tagsPlaceholder = t('expenses.form.placeholders.tags')

	return (
		<form className="space-y-6" onSubmit={handleSubmit}>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.expenseName} *</span>
					<input
						type="text"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.expenseName}
						onChange={e => handleChange('expenseName', e.target.value)}
						required
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.supplier}</span>
					<input
						type="text"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.supplierName || ''}
						onChange={e => handleChange('supplierName', e.target.value)}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.invoiceNumber}</span>
					<input
						type="text"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.invoiceNumber || ''}
						onChange={e => handleChange('invoiceNumber', e.target.value)}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.paymentMethod}</span>
					<select
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.paymentMethod || 'cash'}
						onChange={e => handleChange('paymentMethod', e.target.value)}
					>
						{paymentOptions.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.status}</span>
					<select
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.status || 'draft'}
						onChange={e => handleChange('status', e.target.value)}
					>
						{statusOptions.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.expenseTags}</span>
					<ExpenseTags
						placeholder={tagsPlaceholder}
						selectedTags={selectedTags}
						onTagsChange={handleTagsChange}
					/>
				</label>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.expenseDate} *</span>
					<input
						type="date"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.expenseDate || ''}
						onChange={e => handleChange('expenseDate', e.target.value)}
						required
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.subtotal}</span>
					<input
						type="number"
						step="0.01"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.expenseSubtotal}
						onChange={e => handleChange('expenseSubtotal', e.target.value)}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.taxPercent}</span>
					<input
						type="number"
						step="0.01"
						className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
						value={form.expensePercentTax}
						onChange={e => handleChange('expensePercentTax', e.target.value)}
					/>
				</label>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.taxValue}</span>
					<input
						type="number"
						step="0.01"
						disabled={true}
						className="block w-full rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 cursor-not-allowed"
						value={form.expenseTaxValue}
						onChange={e => handleChange('expenseTaxValue', e.target.value)}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{labels.total}</span>
					<input
						type="number"
						step="0.01"
						value={form.expenseTotal}
						className="block w-full rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 cursor-not-allowed"
						onChange={e => handleChange('expenseTotal', e.target.value)}
					/>
				</label>
			</div>

			<label className="flex flex-col gap-1">
				<span className="text-sm font-medium text-gray-700">{labels.remarks}</span>
				<textarea
					className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
					rows={3}
					value={form.remarks || ''}
					onChange={e => handleChange('remarks', e.target.value)}
				></textarea>
			</label>

			<div className="flex items-center gap-6">
				<label className="flex items-center gap-2 text-sm text-gray-700">
					<input
						type="checkbox"
						checked={!!form.forVat}
						onChange={e => handleCheckbox('forVat', e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
					/>
					<span>{labels.forVat}</span>
				</label>
				<label className="flex items-center gap-2 text-sm text-gray-700">
					<input
						type="checkbox"
						checked={!!form.forCorporateTax}
						onChange={e => handleCheckbox('forCorporateTax', e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
					/>
					<span>{labels.forCorporateTax}</span>
				</label>
			</div>

			<div className="flex justify-end gap-3">
				<button
					type="submit"
					className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
					disabled={submitting}
				>
					{submitting ? submittingLabel : submitLabel}
				</button>
				{onCancel ? (
					<button
						type="button"
						onClick={onCancel}
						className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50"
					>
						{cancelLabel}
					</button>
				) : null}
			</div>
		</form>
	)
}
